/**
 * ConfirmHost — renders the app-wide confirm modal (audit X-7).
 * Mount exactly once, near the app root. See confirmDialog.js for the API.
 */
import React, { useState, useEffect } from 'react';
import useModalBehavior from '../../hooks/useModalBehavior';
import { _registerConfirmHost } from './confirmDialog';
import './ConfirmDialog.css';

export default function ConfirmHost() {
  const [req, setReq] = useState(null); // { opts, resolve }
  const [inputValue, setInputValue] = useState('');

  useEffect(
    () =>
      _registerConfirmHost(
        (opts) =>
          new Promise((resolve) => {
            setInputValue((opts && opts.input && opts.input.defaultValue) || '');
            setReq({ opts, resolve });
          }),
      ),
    [],
  );

  const finish = (val) => {
    setReq((cur) => {
      if (cur) cur.resolve(val);
      return null;
    });
  };

  // Esc / backdrop / focus-trap handling; closing == cancel. For confirms,
  // Cancel gets initial focus (useModalBehavior focuses the first focusable),
  // so a stray Enter can't fire a destructive action; for prompts the input
  // renders first and gets focus instead.
  const isPrompt = !!(req && req.opts && req.opts.input);
  const modal = useModalBehavior(() => finish(isPrompt ? null : false), { enabled: !!req });

  if (!req) return null;
  const o = req.opts || {};
  const danger = o.danger !== false;
  const confirmDisabled = isPrompt && o.input.required && !inputValue.trim();
  const confirm = () => {
    if (confirmDisabled) return;
    finish(isPrompt ? inputValue : true);
  };

  return (
    <div className="cfd-overlay" {...modal.overlayProps}>
      <div
        className={`cfd-modal${danger ? ' cfd-modal--danger' : ''}`}
        {...modal.dialogProps}
        aria-label={o.title || (isPrompt ? 'Input' : 'Confirm')}
      >
        {o.title && <h2 className="cfd-title">{o.title}</h2>}
        {(o.message || !isPrompt) && (
          <p className="cfd-message">{o.message || 'Are you sure?'}</p>
        )}
        {isPrompt && (
          <input
            type="text"
            className="cfd-input"
            value={inputValue}
            placeholder={o.input.placeholder}
            aria-label={o.title || o.message || 'Value'}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                confirm();
              }
            }}
          />
        )}
        <div className="cfd-actions">
          <button
            type="button"
            className="cfd-btn cfd-btn--cancel"
            onClick={() => finish(isPrompt ? null : false)}
          >
            {o.cancelLabel || 'Cancel'}
          </button>
          <button
            type="button"
            className={`cfd-btn ${danger ? 'cfd-btn--danger' : 'cfd-btn--primary'}`}
            disabled={confirmDisabled}
            onClick={confirm}
          >
            {o.confirmLabel || (isPrompt ? 'OK' : 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
